import { useEffect, useState } from 'react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import type { SurveyInstrumentDto } from '@/shared/api/types'
import { addBlock } from './api'
import { PublishedOffer, RetargetedNote, publishedRefusal, type VersionOffer } from './QuestionDialog'

/**
 * A block: a named run of questions.
 *
 * ## Why blocks are worth the extra object
 *
 * Two reasons, and neither is layout. A branch can act on a whole block —
 * `HIDE` the six farming questions for a city respondent — which is one rule
 * instead of six, and one rule that cannot go half-stale when a seventh question
 * is added. And the outline groups by block, so a thirty-question instrument
 * reads as five sections rather than a list nobody can hold in their head.
 *
 * There is no edit or delete here because the API has neither: `POST
 * /survey-versions/{id}/blocks` is the whole surface (contract §3.2). A block
 * written by mistake is corrected by moving its questions off it and leaving it
 * empty — which is worth knowing before rather than after, so the dialog says so.
 */
export function BlockDialog({
  open, versionId, blockCount, offer, onClose, onSaved,
}: {
  open: boolean
  versionId: string
  /** Where an appended block lands, and the default the position field opens on. */
  blockCount: number
  offer: VersionOffer
  onClose: () => void
  onSaved: (instrument: SurveyInstrumentDto) => void
}) {
  const [code, setCode] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [position, setPosition] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<{ sentence: string; versionId: string } | null>(null)

  useEffect(() => {
    if (!open) return
    setCode('')
    setTitle('')
    setDescription('')
    setPosition(String(blockCount + 1))
    setError(null)
    setRefusal(null)
  }, [open, blockCount])

  const movedOn = refusal !== null && refusal.versionId !== versionId

  async function submit() {
    setBusy(true)
    setError(null)
    setRefusal(null)
    try {
      const instrument = await addBlock(versionId, {
        code: code.trim(),
        title: title.trim() || null,
        description: description.trim() || null,
        position: position.trim() === '' ? null : Number(position),
      })
      onSaved(instrument)
    } catch (e) {
      // `survey/block-code-taken` renders as the server wrote it — it names the
      // code and the version, which is everything needed to pick another.
      const frozen = publishedRefusal(e)
      if (frozen) setRefusal({ sentence: frozen, versionId })
      else setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="md"
      title="New block"
      description="A named run of questions. A branch can act on all of them at once."
      footer={(
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            disabled={busy} onClick={submit}>
            {busy ? 'Saving…' : 'Add block'}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />
        {refusal && !movedOn && <PublishedOffer sentence={refusal.sentence} offer={offer} />}
        {movedOn && <RetargetedNote versionNo={offer.nextVersionNo} keyedToARow={false} />}

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-5">
            <Field label="Code" required
              hint="At most 32 characters. Unique inside this version.">
              <input className="erp-input h-9 w-full font-mono text-erp-sm" value={code}
                maxLength={32} placeholder="DEMOG"
                onChange={(e) => setCode(e.target.value)} />
            </Field>
          </div>
          <div className="col-span-4">
            <Field label="Title" hint="What it is called in the outline.">
              <input className="erp-input h-9 w-full" value={title} maxLength={300}
                placeholder="Demographics" onChange={(e) => setTitle(e.target.value)} />
            </Field>
          </div>
          <div className="col-span-3">
            <Field label="Position" hint={`Empty appends at ${blockCount + 1}.`}>
              <input className="erp-input h-9 w-full text-right tabular-nums" type="number" min={1}
                value={position} onChange={(e) => setPosition(e.target.value)} />
            </Field>
          </div>
        </div>

        <Field label="Description"
          hint="For whoever fields this. Why the section exists, what it must not be asked before.">
          <textarea className="erp-input w-full min-h-[64px] py-2" value={description}
            maxLength={4000} onChange={(e) => setDescription(e.target.value)}
            placeholder="Asked last, so a refusal here does not cost the political questions." />
        </Field>

        <Note tone="info">
          Blocks can be added but not edited or deleted — the API has no route for either. A
          block put in by mistake is emptied by moving its questions to another one; an empty
          block asks nobody anything and shows in the outline as what it is.
        </Note>
      </div>
    </Modal>
  )
}
